import { AxiosError, AxiosResponse, RawAxiosResponseHeaders } from 'axios';
import { toaster } from 'components/v2/toast';
import { getAdminIlcdEpdBundle } from 'services/api/adminApi';

type BuildErrorDetails = (message: string, detail: string | undefined) => string;

const UNEXPECTED_ERROR_MESSAGE = 'Unexpected error';

// Composes the error text without an "undefined" suffix when there is no detail.
const buildErrorDetailsDefault: BuildErrorDetails = (message, detail) => (detail ? `${message} (${detail})` : message);

// Reproduces the historical Compiler/Simple Wizard text exactly (SC-005): without a detail the
// literal "undefined" is appended, with an empty-string detail nothing is appended.
const buildLegacyErrorDetails: BuildErrorDetails = (message, detail) => message + (detail && ` (${detail})`);

// Downloads the bundle returned by `fetch`. Never rejects: every failure ends in an error toast.
export async function downloadIlcdEpdBundle(
  fetch: () => Promise<AxiosResponse<Blob>>,
  buildErrorDetails: BuildErrorDetails = buildErrorDetailsDefault
): Promise<void> {
  try {
    const response = await fetch();
    const fileName = getFileNameFromHeaders(response.headers);
    const blob = await response.data;
    const objectUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName ?? 'ILCD.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(objectUrl);
  } catch (e) {
    let details: string;
    if (e instanceof AxiosError) {
      const detail = await readProblemDetail(e);
      details = buildErrorDetails(e.message || UNEXPECTED_ERROR_MESSAGE, detail);
    } else {
      details = (e instanceof Error && e.message) || UNEXPECTED_ERROR_MESSAGE;
    }
    toaster({
      severity: 'error',
      summary: 'Downloading error',
      details,
    });
  }
}

export default async function downloadLcdEpdByAdmin(epdVersionId: string) {
  await downloadIlcdEpdBundle(() => getAdminIlcdEpdBundle(epdVersionId), buildLegacyErrorDetails);
}

// Reads `detail` from a ProblemDetails Blob body. A missing, non-Blob or non-JSON body
// (for example an HTML gateway page) yields no detail instead of throwing.
async function readProblemDetail(error: AxiosError): Promise<string | undefined> {
  try {
    const responseText = await (error.response?.data as Blob | undefined)?.text();
    const responseData = responseText ? JSON.parse(responseText) : null;
    return responseData?.detail;
  } catch {
    return undefined;
  }
}

function getFileNameFromHeaders(headers: RawAxiosResponseHeaders): string | null {
  const contentDisposition = headers['content-disposition'];
  if (typeof contentDisposition !== 'string') {
    return null;
  }
  // Regexp based on: https://stackoverflow.com/a/23054920
  const filenameRegex = /filename[^;=\n]*=((['"])(.*?)\2|[^;\n]*)/;
  const match = contentDisposition.match(filenameRegex);
  if (!match) {
    return null;
  }
  return match[3];
}
